import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { HOME_SCROLL_CONTAINER_ID } from '@/constants/homeScroll';
import ModalLoginSuccess from '../web/components/ModalLoginSuccess';
import ModalLoginAndJoinCampaign from '../web/components/ModalLoginAndJoinCampaign';
import ModalRefuseCampaign from '../web/components/ModalRefuseCampaign';
import ModalNotInterestSubmit from '../web/components/ModalNotInterestSubmit';
import { isMobile } from '@/biz/tool';
import ContactInformationModal from '../web/components/contactInformationModal';
import ModalSelectInvitedOrder from '../web/components/ModalSelectInvitedOrder';
import ModalAnalysis from '../web/components/ModalAnalysis';
import ModalConfirmDealPrice from '../web/components/ModalConfirmDealPrice';
import ModalSubmitInterest from '../web/components/ModalSubmitInterest';
import ModalMissOut from '../web/components/ModalMissOut';
import ModalSelectInvitedOrderByNoMatch from '../web/components/ModalSelectInvitedOrderByNoMatch';
import ModalAddress from '../web/components/ModalAddress';
import ModalWithdrawThisCollaboration from '../web/components/ModalWithdrawThisCollaboration';
import ModalDealWithdrawn from '../web/components/ModalDealWithdrawn';
import ModalEducation from '../web/components/ModalEducation';
import ModalAuthorizationCode from '../web/components/ModalAuthorizationCode';
import ModalPublishTime from '../web/components/ModalPublishTime';
import ModalPromotionalLinkEducation from '../web/components/ModalPromotionalLinkEducation';
import ModalCreditScore from '../web/components/ModalCreditScore';
import ModalEmailVerification from '../web/components/ModalEmailVerification';
import ModalNotMatchTips from '../web/components/ModalNotMatchTips';
import ModalShowImage from '../web/components/ModalShowImage';
import ModalPausedTip from '../web/components/ModalPausedTip';
import ModalSoftDeleteTip from '../web/components/ModalSoftDeleteTip';
import ModalTakeOrder from '../web/components/ModalTakeOrder';

/**
 * 与主内容区 #home-scroll-container（MobileListRefresh / 首页）同步。
 * - 路由变化 / 懒加载后需重新绑定；PC 端列表滚动在 overflow 容器上，须与 window 双监听。
 * - 每次计算 scroll 时重新 getElementById，避免闭包拿到旧节点。
 * - 按钮挂到 document.body，避免祖先 transform/overflow 裁剪 fixed。
 */
function ScrollToTop() {
  const [visible, setVisible] = useState(false);
  const { pathname, search } = useLocation();

  useEffect(() => {
    let detach: (() => void) | undefined;
    let rafId = 0;
    let tryCount = 0;
    const maxTries = 90;

    const bind = () => {
      if (detach) detach();
      const container = document.getElementById(HOME_SCROLL_CONTAINER_ID);

      const toggleVisible = () => {
        const el = document.getElementById(HOME_SCROLL_CONTAINER_ID);
        const windowScroll =
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          document.body.scrollTop;
        const containerScroll = el?.scrollTop ?? 0;
        setVisible(Math.max(windowScroll, containerScroll) > 100);
      };

      toggleVisible();
      window.addEventListener('scroll', toggleVisible, { passive: true });
      const cleanups: Array<() => void> = [
        () => window.removeEventListener('scroll', toggleVisible)
      ];
      if (container) {
        container.addEventListener('scroll', toggleVisible, { passive: true });
        cleanups.push(() =>
          container.removeEventListener('scroll', toggleVisible)
        );
      }
      detach = () => cleanups.forEach((fn) => fn());

      const needRetry = !container && tryCount < maxTries;
      if (needRetry) {
        tryCount += 1;
        rafId = requestAnimationFrame(bind);
      }
    };

    bind();
    const t1 = window.setTimeout(bind, 400);
    const t2 = window.setTimeout(bind, 1800);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      cancelAnimationFrame(rafId);
      detach?.();
    };
  }, [pathname, search]);

  const scrollToTop = () => {
    const container = document.getElementById(HOME_SCROLL_CONTAINER_ID);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    container?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!visible) return null;

  const button = (
    <div
      onClick={scrollToTop}
      className={`fixed w-10 h-10 bg-white rounded-full shadow-lg cursor-pointer flex items-center justify-center hover:bg-gray-50 transition-all z-[9999]
        ${isMobile() ? 'bottom-[calc(74px+env(safe-area-inset-bottom,0px))] right-[20px]' : 'bottom-[calc(82px+env(safe-area-inset-bottom,0px))] right-[20px]'}
        `}
    >
      <svg
        className="w-5 h-5 text-gray-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 10l7-7m0 0l7 7m-7-7v18"
        />
      </svg>
    </div>
  );

  if (typeof document === 'undefined' || !document.body) return button;

  return createPortal(button, document.body);
}

export default function ModalBox() {
  return (
    <>
      {/* 登录成功弹窗 */}
      <ModalLoginSuccess />
      {/* 登录并加入活动弹窗 */}
      <ModalLoginAndJoinCampaign />
      {/* 拒绝订单 */}
      <ModalRefuseCampaign />
      {/* 不感兴趣 提交后弹窗 */}
      <ModalNotInterestSubmit />
      {/* 回到顶部按钮 */}
      <ScrollToTop />
      {/* 添加新的联系方式弹窗 */}
      <ContactInformationModal />
      {/* 加入后 选择邀请订单弹窗 */}
      <ModalSelectInvitedOrder />
      {/* 加入弹窗 1. */}
      <ModalAnalysis />
      {/* 改价弹窗 2. */}
      <ModalConfirmDealPrice />
      {/* 确认加入 3. */}
      <ModalSubmitInterest />
      {/* 接单 PC端总弹窗 */}
      <ModalTakeOrder />
      {/* 挽留弹窗 */}
      <ModalMissOut />
      {/* 选择邀请订单弹窗 success | noMatch */}
      <ModalSelectInvitedOrderByNoMatch />
      {/* 实体地址弹窗 */}
      <ModalAddress />
      {/*  */}
      {/* 撤回申请弹窗 */}
      <ModalWithdrawThisCollaboration />
      <ModalDealWithdrawn />
      {/* 达人逾期教育弹窗 */}
      <ModalEducation />
      {/* TT instagram authorization code 教育弹窗 */}
      <ModalAuthorizationCode />
      {/* 发布时间提示弹窗 */}
      <ModalPublishTime />
      {/* Promotional link Education信用分弹窗 */}
      <ModalPromotionalLinkEducation />
      {/* 信用分弹窗 */}
      <ModalCreditScore />
      {/* 邮箱验证弹窗 */}
      <ModalEmailVerification />
      {/* 不匹配提示弹窗 */}
      <ModalNotMatchTips />
      {/* 图片放大预览 */}
      <ModalShowImage />
      {/* 活动暂停提示弹窗 */}
      <ModalPausedTip />
      {/* 软删订单提示弹窗 */}
      <ModalSoftDeleteTip />
    </>
  );
}
